import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { reportImageDownloadFailure } from '~/hooks/image/reportImageDownloadFailure';
import useLoadAvailableImage from '~/hooks/image/useLoadAvailableImage';
import type { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import { gridImageRowsRequestState } from '~/state/atoms/viewportGridAtoms';

/**
 * Opens images of the patient in a grid row each. An image known only from
 * the session is downloaded into the patient first, without changing the
 * primary image, while the busy indicator shows. The images load one after
 * another, as an import refuses to start while another is parsing. An
 * image whose download fails gets no row and the user is told why; the
 * others still open.
 */
export default function useOpenImageRows(
    images: ReadonlyArray<AvailableImage>
): (seriesInstanceUids: ReadonlyArray<SeriesInstanceUid>) => Promise<void> {
    const intl = useIntl();
    const loadAvailableImage = useLoadAvailableImage();
    const { showBusy, hideBusy } = useBusyIndicator();
    const requestImageRows = useSetRecoilState(gridImageRowsRequestState);

    return useCallback(
        async (seriesInstanceUids) => {
            const toLoad = images.filter(
                (image) =>
                    !image.imageObject &&
                    seriesInstanceUids.some(
                        (uid) => uid === image.seriesInstanceUID
                    )
            );
            const failed = new Set<string>();
            if (toLoad.length > 0) {
                await showBusy(
                    intl.formatMessage({
                        id: 'datasetPicker.downloadingImages',
                    })
                );
                try {
                    for (const image of toLoad) {
                        try {
                            if (await loadAvailableImage(image)) {
                                continue;
                            }
                            failed.add(image.seriesInstanceUID);
                            reportImageDownloadFailure(intl);
                        } catch (error) {
                            failed.add(image.seriesInstanceUID);
                            reportImageDownloadFailure(intl, error);
                        }
                    }
                } finally {
                    hideBusy();
                }
            }
            requestImageRows({
                seriesInstanceUids: seriesInstanceUids.filter(
                    (uid) => !failed.has(uid)
                ),
            });
        },
        [hideBusy, images, intl, loadAvailableImage, requestImageRows, showBusy]
    );
}
